<template>
  <div class="content-with-spacing">
    <div v-if="ready && meetingForm" id="meeting-form-page">
      <section class="heading-container" :class="{ dashboard: !isApp }">
        <div class="heading-content">
          <div class="title-container">
            <h1>{{ meetingForm.name }}</h1>
            <p class="description" v-html="getMarkdown(meetingForm.description)"></p>
          </div>
          <div v-if="!isApp" class="heading-details">
            <p v-if="latestSubmission" class="submission-date">Last submission: <b>{{parseTimestamp(latestSubmission.date_timestamp)}}</b></p>
          </div>
          <div v-if="!isApp && studentInfo" class="profile">
            <user-profile-card
              :name="studentInfo.full_name"
              :role="'Learner'"
              :avatarUrl="studentInfo.avatar_url"
              :email="studentInfo.email"
            ></user-profile-card>
          </div>
        </div>
      </section>
      <section>
        <empty-placeholder v-if="!latestSubmission && isApp" title="No submission" info="The meeting form has not been submitted yet."></empty-placeholder>
        <k-meeting-form v-else
          ref="meetingForm"
          :questionnaire="meetingForm"
          :latest-submission="latestSubmission"
          :read-only="isReadOnly"
          :disable-text-toolbar="isApp"
          >
          <template v-if="!isReadOnly" #actions>
            <div class="button-container">
              <button @click.prevent="clear" class="btn btn-secondary reset-button" type="button" title="Reset responses">Reset</button>
              <button @click.prevent="submit" class="btn btn-primary submit-button" :disabled="disableSubmit">Submit Responses</button>
            </div>
          </template>
          <template #bottom-actions>
            <div class="sign-meeting-form form-required">
              <label>
                <input type="checkbox" v-model="signedMeetingForm"/>I confirm that these notes are a true and accurate account of this meeting, and acknowledge that they will be retained as part of the learning record.
              </label>
              <button v-if="isApp" class="btn btn-success" type="button" @click.prevent="signMeetingForm" :disabled="!signedMeetingForm || hasLatestSubmissionBeenSignedByUser">Sign Meeting Form</button>
            </div>
            <div v-if="!isReadOnly" class="submit-form-button bottom-submit-button">
              <button @click.prevent="submit" class="btn btn-primary submit-button" :disabled="disableSubmit">Submit Responses</button>
              <button @click.prevent="saveDraft" class="btn btn-default save-draft-button">Save as Draft</button>
            </div>
          </template>
        </k-meeting-form>
        </section>
      </div>
    </div>
</template>

<style scoped>
.sign-meeting-form {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sign-meeting-form input {
  margin-right: 5px;
}

.sign-meeting-form label {
  cursor: pointer;
  align-self: flex-start;
}

.sign-meeting-form label:hover {
  text-decoration: underline;
}

.sign-meeting-form button {
  margin-left: 0;
  margin-top: 10px;
  align-self: center;
}

.button-container {
  display: flex;
  justify-content: right;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-bottom: 15px;
}

.submit-form-button {
  position: relative;
  display: inline-block;
}

.submit-form-button button {
  padding: 10px 25px;
}

.bottom-submit-button {
  gap: 10px;
  display: flex;
  justify-content: center;
  width: 100%;
}
</style>

<script>
import useGlobalStore from '@stores/global';
import TimeMixins from '@mixins/time-mixins';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import { markdown } from '@utils/maths-markdown';
import KMeetingForm from '../k-questionnaire.vue';
import UserProfileCard from '../../components/user-profile-card.vue';

export default {
  mixins: [TimeMixins],

  components: {
    UserProfileCard,
    KMeetingForm,
    EmptyPlaceholder,
  },

  props: {
    isAdmin: {
      type: Boolean,
      default: false,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      store: useGlobalStore(),
      meetingFormReady: false,
      meetingForm: undefined,
      latestSubmissionReady: false,
      latestSubmission: undefined,
      isSubmitting: false,
      disableSubmission: true,
      signedMeetingForm: false,
      studentInfo: undefined,
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getMeetingForm();
    this.getLatestSubmission();
    if (this.studentId) {
      this.getStudentData();
    }
    if (this.meetingFormReady) {
      this.registerCrumbs();
    }
  },

  watch: {
    ready(val) {
      if (val) {
        this.$Loading.finish();
        this.$nextTick(() => {
          this.disableSubmission = false;
        });
      } else {
        this.$Loading.start();
      }
    },
    isSubmitting(val) {
      if (val) {
        this.$Loading.minimal();
      } else {
        this.$Loading.finish();
      }
    },
    latestSubmission() {
      this.signedMeetingForm = this.hasLatestSubmissionBeenSignedByUser;
    },
    studentId(val) {
      if (val) {
        this.getStudentData();
      }
    },
    meetingFormReady(val) {
      if (val) {
        this.registerCrumbs();
      }
    },
  },

  computed: {
    ready() {
      return this.meetingFormReady && this.latestSubmissionReady;
    },
    isApp() {
      return this.store.isApp;
    },
    meetingId() {
      return parseInt(this.$route.params.moduleMeetingId, 10);
    },
    studentId() {
      if (this.isApp) {
        return this.store.userId;
      }
      return this.$route.params.studentId;
    },
    submissionEndpoint() {
      const urlArg = this.isReadOnly ? '?is_draft=false' : '';
      if (this.isApp) {
        return `/api/curriculum/meeting/${this.meetingId}/submission`;
      } if (this.isAdmin) {
        return `/api/admin/curriculum/meeting/${this.meetingId}/user/${this.studentId}/submission${urlArg}`;
      }
      return `/api/curriculum/meeting/${this.meetingId}/user/${this.studentId}/submission${urlArg}`;
    },
    hasLatestSubmissionBeenSignedByUser() {
      if (!this.latestSubmission || !Array.isArray(this.latestSubmission.signatures)) {
        return false;
      }
      return this.latestSubmission.signatures.some(sig => sig?.signee_user_id === parseInt(this.store.userId, 10));
    },
    disableSubmit() {
      // Submission is disabled if the meeting form has not been signed by the user or the latest submission has not been signed by the user
      return !(this.signedMeetingForm || this.hasLatestSubmissionBeenSignedByUser);
    },
  },

  methods: {
    submit() {
      const payload = this.$refs.meetingForm.getSubmitPayload();
      payload.is_draft = false;
      this.submitAndSignMeetingForm(payload);
    },
    saveDraft() {
      const payload = this.$refs.meetingForm.getSubmitPayload();
      payload.is_draft = true;
      this.submitAndSignMeetingForm(payload);
    },
    clear() {
      this.$refs.meetingForm.clearResponses();
    },
    registerCrumbs() {
      if (!this.isApp) {
        let crumbs;
        if (this.isReadOnly) {
          crumbs = [
            {
              text: 'Dashboard Students',
              path: {
                name: 'dashboard_students',
              },
              query: {
                studentId: this.studentId,
              },
            },
            { text: this.meetingForm.name, active: true },
          ];
        } else {
          crumbs = [
            {
              text: 'Meetings Inbox',
              path: {
                name: 'meetings_inbox',
              },
            },
            { text: this.meetingForm.name, active: true },
          ];
        }
        this.$crumbs.register(crumbs);
      }
    },

    signingEndpoint(submissionId) {
      if (this.isApp) {
        return `/api/curriculum/meeting/${this.meetingId}/submission/${submissionId}/signature`;
      }
      return `/api/curriculum/admin/meeting/${this.meetingId}/user/${this.studentId}/submission/${submissionId}/signature`;
    },

    getMeetingForm() {
      this.$logger.info('Getting meeting form info', { meetingId: this.meetingId }, true);
      this.$http.get(`/api/curriculum/meeting/${this.meetingId}/form`).then(response => {
        this.$logger.info('Got meeting form info', { meetingId: this.meetingId });
        this.meetingForm = response.data;
        this.meetingFormReady = true;
      }).catch(err => {
        if (this.$http.isWarning(err)) {
          this.$logger.warn('Meeting form not found', { meetingId: this.meetingId }, err);
        } else {
          this.$logger.error('Error getting meeting form', { meetingId: this.meetingId }, err);
          this.$ktoast.showError(err, true);
        }
      });
    },

    getLatestSubmission() {
      this.$logger.info('Getting latest meeting form submission', { meetingId: this.meetingId }, true);
      this.$http.get(this.submissionEndpoint).then(response => {
        this.$logger.info('Got latest meeting form submission', { meetingId: this.meetingId });
        this.latestSubmission = response.data;
        this.previousResponses = this.latestSubmission?.responses;
      }).catch(err => {
        if (this.$http.isWarning(err)) {
          this.$logger.warn('Latest meeting form submission not found', { meetingId: this.meetingId }, err);
        } else {
          this.$logger.error('Error getting latest meeting form submission', { meetingId: this.meetingId }, err);
          this.$ktoast.showError(err, true);
        }
      }).then(() => {
        this.latestSubmissionReady = true;
      });
    },

    submitMeetingForm(payload) {
      if (this.isApp || this.disableSubmission) {
        return Promise.resolve();
      }
      this.$logger.info('Submitting meeting form', { meetingId: this.meetingId }, true);
      return this.$http.post(`/api/curriculum/meeting/${this.meetingId}/user/${this.studentId}/submission`, payload);
    },

    signatureRequest(submissionId) {
      this.$logger.info('Signing meeting form', { meetingId: this.meetingId }, true);
      return this.$http.post(this.signingEndpoint(submissionId));
    },

    submitAndSignMeetingForm(payload) {
      this.isSubmitting = true;
      this.submitMeetingForm(payload)
        .then(response => {
          if (payload.is_draft) {
            this.$logger.info('Draft submission, skipping signature', { meetingId: this.meetingId });
            return Promise.resolve();
          }
          return this.signatureRequest(response.data.id)
            .catch(err => {
              this.$logger.error('Error signing meeting form', { meetingId: this.meetingId }, err);
              this.$ktoast.showError(err, true);
            });
        })
        .then(() => {
          this.$ktoast.success('Meeting form submitted');
        })
        .catch(err => {
          this.$logger.error('Error submitting and signing meeting form', { meetingId: this.meetingId }, err);
          this.$ktoast.showError(err, true);
        })
        .then(() => {
          this.isSubmitting = false;
          this.getLatestSubmission();
        });
    },

    signMeetingForm() {
      // For learner on app only
      this.isSubmitting = true;
      if (!this.latestSubmission?.meeting_form_submission_id) {
        this.$logger.error('Cannot sign meeting form as no submission is available.', { meetingId: this.meetingId });
        this.$ktoast.showError('Cannot sign meeting form, no submission found.', true);
        this.isSubmitting = false;
        return;
      }
      this.signatureRequest(this.latestSubmission.meeting_form_submission_id).then(() => {
        this.$ktoast.success('Meeting form signed');
      }).catch(err => {
        this.$logger.error('Error signing meeting form', { meetingId: this.meetingId }, err);
        this.$ktoast.showError(err, true);
      }).then(() => {
        this.isSubmitting = false;
        this.getLatestSubmission();
      });
    },

    getMarkdown(str) {
      return markdown(str);
    },

    getStudentData() {
      if (this.isApp) {
        return;
      }
      this.$logger.info('Getting student profile', { studentId: this.studentId }, true);
      this.$http.get(`/api/profile/users/${this.studentId}`)
        .then(result => {
          this.studentInfo = result.data;
          this.$logger.info('Got student profile', { studentId: this.studentId });
        }).catch(error => {
          this.$logger.info('Error getting student profile', { studentId: this.studentId }, error);
          this.$ktoast.showError(error);
        });
    },
  },
};
</script>
